import React from "react";
import { useTranslation } from "react-i18next";

function formatDecimal(value, digits = 3) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toFixed(digits).replace(/\.?0+$/, "");
}

function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(2)} PLN` : String(value);
}

function StockPostingResultSummary({ result, onDismiss, onShowHistory }) {
  const { t } = useTranslation(["common", "stock"]);
  if (!result) return null;

  const unit = result.unit || "";
  const directionClass = result.changeKind ? ` stock-posting-result__change--${result.changeKind}` : "";
  const quantityPrefix = result.changePrefix || "";

  return (
    <section className="stock-posting-result" aria-live="polite" aria-label={t("stock:postingResult.ariaLabel")}>
      <div className="stock-posting-result__header">
        <div>
          <span className="stock-posting-result__eyebrow">{t("stock:postingResult.eyebrow")}</span>
          <h2>{t("stock:postingResult.title", { id: result.movementId })}</h2>
          <p>{result.product?.name_public || result.productName || "—"}</p>
        </div>
        <div className="stock-posting-result__header-actions">
          {onShowHistory && (
            <button type="button" className="stock-btn stock-btn--outline" onClick={onShowHistory}>
              {t("stock:postingResult.showHistory")}
            </button>
          )}
          <button
            type="button"
            className="stock-posting-result__dismiss"
            onClick={onDismiss}
            aria-label={t("common:actions.close")}
            title={t("common:actions.close")}
          >
            ×
          </button>
        </div>
      </div>

      {result.postingContext && <p>{t("stock:unifiedCore.currentAtCommit")}</p>}
      <div className="stock-posting-result__grid">
        <div>
          <span>{t("stock:postingResult.change")}</span>
          <strong className={`stock-posting-result__change${directionClass}`}>
            {quantityPrefix}{formatDecimal(result.quantity, 3)} {unit}
          </strong>
        </div>

        {Array.isArray(result.locationBalances) && result.locationBalances.map((location) => (
          <div key={`${location.id || location.name}-${location.role || "location"}`}>
            <span>{location.role === "from" ? t("stock:postingResult.fromLocationAfter") : location.role === "to" ? t("stock:postingResult.toLocationAfter") : t("stock:postingResult.locationAfter")}</span>
            <strong>{location.name || "—"}: {formatDecimal(location.quantity, 3)} {unit}</strong>
          </div>
        ))}

        <div>
          <span>{t("stock:postingResult.productStockAfter")}</span>
          <strong>{formatDecimal(result.productQuantityAfter, 3)} {unit}</strong>
        </div>

        <div>
          <span>{t("stock:postingResult.averageCostAfter")}</span>
          <strong>{result.averageCostAfter == null ? "—" : `${formatDecimal(result.averageCostAfter, 4)} PLN/${unit || "unit"}`}</strong>
        </div>

        <div>
          <span>{t("stock:postingResult.productValueAfter")}</span>
          <strong>{formatMoney(result.productValueAfter)}</strong>
        </div>
      </div>
      {result.postingContext?.upgraded_from_legacy && <p role="status">{t("stock:unifiedCore.upgradeNotice")}</p>}
      {result.postingContext?.revalued_movements > 0 && <p>{t("stock:unifiedCore.revaluedNotice", { count: result.postingContext.revalued_movements })}</p>}
      {result.postingContext?.historical && (
        <details className="stock-audit-detail__section">
          <summary>{t("stock:unifiedCore.operationAtDate", { date: result.businessDate })}</summary>
          <p>{t("stock:unifiedCore.contextHelp")}</p>
          <div className="stock-posting-result__grid">
            <div><span>{t("stock:unifiedCore.productAtDate")}</span><strong>{formatDecimal(result.postingContext.operation_after?.productCost?.total_quantity, 6)} {unit}</strong></div>
            <div><span>{t("stock:unifiedCore.valueAtDate")}</span><strong>{formatMoney(result.postingContext.operation_after?.productCost?.total_value_net)}</strong></div>
            <div><span>{t("stock:unifiedCore.averageAtDate")}</span><strong>{formatDecimal(result.postingContext.operation_after?.productCost?.average_cost_net, 4)} PLN/{unit}</strong></div>
          </div>
        </details>
      )}
    </section>
  );
}

export default StockPostingResultSummary;
